import { gql, useMutation } from '@apollo/client';
import React from 'react';
import { useDispatch } from 'react-redux';
import type {
  EditTopicModal_TopicFragment,
  UpdateTopicMutation,
  UpdateTopicMutationVariables,
} from '../../graphql/generated/graphqlTypes';
import UPDATE_TOPIC_MUTATION from '../../graphql/updateTopicMutation';
import UIActions from '../../ui/UIActions';
import topicPath from '../../utils/url/topicPath';
import { useRouter } from '../../utils/router/v3Bridge';
import useDeleteTopic from '../useDeleteTopic';
import TopicModal from './TopicModal';

const TOPIC_FRAGMENT = gql`
  fragment EditTopicModal_topic on Topic {
    id
    name
    parent {
      id
    }
    privacy
    memberEditable
  }
`;

interface EditTopicModalProps {
  close(): void;
  topic: EditTopicModal_TopicFragment;
}

const EditTopicModal = ({
  close,
  topic: { id, name, parent, privacy, memberEditable },
}: EditTopicModalProps) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const parentId = parent?.id;
  const [updateTopic] = useMutation<
    UpdateTopicMutation,
    UpdateTopicMutationVariables
  >(UPDATE_TOPIC_MUTATION);
  const deleteTopic = useDeleteTopic(id);

  const onSubmit = (
    values: { name: string } & Omit<UpdateTopicMutationVariables, 'id'>,
  ) =>
    updateTopic({
      variables: {
        id,
        ...values,
        privacy: privacy !== values.privacy ? values.privacy : undefined,
        description: JSON.stringify(values.description),
      },
    })
      .then(() => dispatch(UIActions.closeModal(true)))
      .then(() => router.push(topicPath({ id, name: values.name })));

  return (
    <TopicModal
      close={close}
      id={id}
      name={name}
      onDelete={deleteTopic}
      onSubmit={onSubmit}
      parentId={parentId}
      privacy={privacy}
      memberEditable={memberEditable}
      submitText="Save"
      title="Edit Topic"
    />
  );
};

EditTopicModal.fragments = {
  topic: TOPIC_FRAGMENT,
};

export default EditTopicModal;
